import { SupplierShape } from './supplier-types';
import csv2json from 'csvtojson';
import { handlePreprocessConversions } from '../../helpers/handlePreprocessConversions';
import { TUNING777_PRICELIST_RULESET } from './777tuning/pricelist';
import { TUNING777_STOCKLIST_RULESET } from './777tuning/stocklist';
import { TRule } from '../typings';

export const TUNING777_SUPPLIER: SupplierShape = {
    weight: 10,
    override: true,
    ruleset: [] as TRule[],
    pricelistRuleset: TUNING777_PRICELIST_RULESET,
    stockRuleset: TUNING777_STOCKLIST_RULESET,
    canSaveWithoutNecessaryFields: true,
    canSaveWithoutEan: false,
    stockDefaults: { model: {}, product: {} },
    pricelistDefaults: { model: {}, product: {} },
    catalogDefaults: {
        product: {
            productName2: '',
            dirtyFields: '{}',
        },
        model: {
            season: '',
            usage: null,
            dataSource: '777TUNING',
            dirtyFields: '{}',
        },
    },
    preprocessKeys: {
        price: {
            ',': '.',
        },
    },
    hardCodes: { 1: { product: {}, model: {} } },
    async preprocessPricelist(file: string) {
        const json = await csv2json({ delimiter: ';' }).fromString(file);
        
        // Get current year (last 2 digits)
        const currentYear2d = new Date().getFullYear() % 100;

        // Filter items based on SKU checks
        const filteredJson = json.filter((item) => {
            // Exclude DEMO SKUs
            const sku = item.sku?.trim().toUpperCase() || '';
            if (sku.includes('DEMO')) {
                return false;
            }

            // Check SKU suffix for year pattern "/YY" or "//YY"
            // Match one or more slashes followed by exactly two digits at end of string
            const yearMatch = sku.match(/\/+(\d{2})$/);
            if (yearMatch) {
                const skuYear = parseInt(yearMatch[1], 10);
                // Compute age difference modulo 100 (handles wrap-around)
                const age = (currentYear2d - skuYear + 100) % 100;
                // Skip if older than 2 years
                if (age >= 2) {
                    return false;
                }
            }

            return true;
        });

        // Group by EAN and select the cheapest item for each EAN
        const eanMap = new Map();
        filteredJson.forEach((item) => {
            const ean = item['ean'];
            const price = parseFloat(item['price'].replace(',', '.'));
            
            if (!eanMap.has(ean) || parseFloat(eanMap.get(ean)['price'].replace(',', '.')) > price) {
                eanMap.set(ean, item);
            }
        });
        
        // Convert map back to array
        const dedupedJson = Array.from(eanMap.values());

        return handlePreprocessConversions(dedupedJson, this.preprocessKeys);
    },
    async preprocessStockList(file: string) {
        const json = await csv2json({ delimiter: ';' }).fromString(file);
        
        // Get current year (last 2 digits)
        const currentYear2d = new Date().getFullYear() % 100;

        // Filter items based on SKU checks
        const filteredJson = json.filter((item) => {
            // Exclude DEMO SKUs
            const sku = item.sku?.trim().toUpperCase() || '';
            if (sku.includes('DEMO')) {
                return false;
            }

            // Check SKU suffix for year pattern "/YY" or "//YY"
            // Match one or more slashes followed by exactly two digits at end of string
            const yearMatch = sku.match(/\/+(\d{2})$/);
            if (yearMatch) {
                const skuYear = parseInt(yearMatch[1], 10);
                // Compute age difference modulo 100 (handles wrap-around)
                const age = (currentYear2d - skuYear + 100) % 100;
                // Skip if older than 2 years
                if (age >= 2) {
                    return false;
                }
            }

            return true;
        });
        
        // Group by EAN and select the cheapest item for each EAN
        const eanMap = new Map();
        filteredJson.forEach((item) => {
            const ean = item['ean'];
            const price = parseFloat(item['price'].replace(',', '.'));
            
            if (!eanMap.has(ean) || parseFloat(eanMap.get(ean)['price'].replace(',', '.')) > price) {
                eanMap.set(ean, item);
            }
        });
        
        // Convert map back to array
        const dedupedJson = Array.from(eanMap.values());

        return handlePreprocessConversions(dedupedJson, this.preprocessKeys);
    },
    async preprocessCatalog(file: string) {
        const json = await csv2json({ delimiter: ';' }).fromString(file);

        return handlePreprocessConversions(json, this.preprocessKeys);
    },
}; 